// ToastService.js
import '../components/ToastStyles.css';
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaInfoCircle, 
  FaTimes 
} from 'react-icons/fa';
import ReactDOM from 'react-dom/client';
import React, { useEffect, useState } from 'react';

// Main container component
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Handle toast events for non-React components
    const handleToastEvent = (event) => {
      const { type, message, options } = event.detail;
      addToast(message, { ...options, type });
    };

    document.addEventListener('toast', handleToastEvent);
    
    return () => {
      document.removeEventListener('toast', handleToastEvent);
    };
  }, []);

  // Add a new toast
  const addToast = (message, options = {}) => {
    const id = Date.now().toString();
    const newToast = {
      id,
      message,
      type: options.type || 'info',
      duration: options.duration || 5000
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  };

  // Remove a toast
  const removeToast = (id) => {
    setToasts(prev => {
      // First mark as exiting for animation
      return prev.map(toast => 
        toast.id === id ? { ...toast, exiting: true } : toast
      );
    });

    // After animation delay, actually remove
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300); // Match animation duration
  };

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          exiting={toast.exiting}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

// Individual toast notification
const ToastNotification = ({ id, message, type, exiting, onClose }) => {
  // Icon based on type
  let icon;
  switch (type) {
    case 'success':
      icon = <FaCheckCircle />;
      break;
    case 'error':
      icon = <FaExclamationTriangle />;
      break;
    case 'warning':
      icon = <FaExclamationTriangle />;
      break;
    case 'info':
    default:
      icon = <FaInfoCircle />;
      break;
  }

  return (
    <div className={`toast-notification ${type} ${exiting ? 'exiting' : ''}`}>
      <div className="toast-icon">{icon}</div>
      <div className="toast-message">{message}</div>
      <button className="toast-close" onClick={() => onClose(id)} aria-label="Close">
        <FaTimes />
      </button>
    </div>
  );
};

// Initialize the container
let containerRoot = null;

const initializeContainer = () => {
  if (typeof window === 'undefined') return;
  
  // Check if container already exists
  let containerElement = document.getElementById('toast-container-root');
  
  if (!containerElement) {
    containerElement = document.createElement('div');
    containerElement.id = 'toast-container-root';
    document.body.appendChild(containerElement);
  }
  
  if (!containerRoot) {
    try {
      containerRoot = ReactDOM.createRoot(containerElement);
      containerRoot.render(<ToastContainer />);
    } catch (error) {
      console.error('Failed to initialize toast container:', error);
    }
  }
};

// Initialize on import
initializeContainer();

// Toast service API
const toast = {
  success: (message, options = {}) => {
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('toast', { 
        detail: { type: 'success', message, options } 
      });
      document.dispatchEvent(event);
    }
  },
  
  error: (message, options = {}) => {
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('toast', { 
        detail: { type: 'error', message, options } 
      });
      document.dispatchEvent(event);
    }
  },
  
  warning: (message, options = {}) => {
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('toast', { 
        detail: { type: 'warning', message, options } 
      });
      document.dispatchEvent(event);
    }
  },
  
  info: (message, options = {}) => {
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('toast', { 
        detail: { type: 'info', message, options } 
      });
      document.dispatchEvent(event);
    }
  }
};

export default toast;